import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { db } from "../lib/db";
import { logAudit } from "../utils/auditLog";
import { apiFetch, getCurrentUserInfo, getCurrentPracticeId } from "../lib/api";
import { ORTHO_ICD10 } from "../data/orthoData";
import { MedSelect } from "./ASCFormInputs";
import { MEDICATIONS, searchMedications, groupByCategory } from "../data/medications";
import ERxModal from "./ERxModal";
import { getErxVendor } from "../lib/erx";
import { notify } from "../utils/notify";
import { useApp } from "../context/AppContext";
import { todayISO } from "../utils/chartHelpers";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:   "var(--navy)", navy2: "var(--navy-2)", navy3: "var(--navy-3)", navy4: "var(--navy-4)",
  teal:   "#0ea5a0", tealLt: "rgba(14,165,160,0.12)",
  amber:  "#f0a500", amberLt: "rgba(240,165,0,0.15)",
  red:    "#e05252", redLt: "rgba(224,82,82,0.12)",
  green:  "#22c55e", greenLt: "rgba(34,197,94,0.12)",
  text1:  "var(--text-1)", text2: "var(--text-2)", text3: "var(--text-3)",
  border: "var(--border)", border2: "var(--border-2)",
};

// ─── DRUG DATABASE ────────────────────────────────────────────────────────────
const ORTHO_DRUGS = [
  { name:"Oxycodone",                    strengths:["5mg","10mg","15mg","20mg"],  forms:["tablet"],             controlled:true,  schedule:"C-II"  },
  { name:"Hydrocodone/Acetaminophen",    strengths:["5/325mg","7.5/325mg","10/325mg"], forms:["tablet"],        controlled:true,  schedule:"C-III" },
  { name:"Tramadol",                     strengths:["50mg","100mg"],              forms:["tablet"],             controlled:true,  schedule:"C-IV"  },
  { name:"Gabapentin",                   strengths:["100mg","300mg","600mg"],     forms:["capsule","tablet"],   controlled:true,  schedule:"C-V"   },
  { name:"Carisoprodol",                 strengths:["250mg","350mg"],             forms:["tablet"],             controlled:true,  schedule:"C-IV"  },
  { name:"Meloxicam",                    strengths:["7.5mg","15mg"],              forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Naproxen",                     strengths:["250mg","375mg","500mg"],     forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Diclofenac",                   strengths:["50mg","75mg"],              forms:["tablet"],              controlled:false, schedule:null    },
  { name:"Ibuprofen",                    strengths:["400mg","600mg","800mg"],    forms:["tablet"],              controlled:false, schedule:null    },
  { name:"Celecoxib",                    strengths:["100mg","200mg"],            forms:["capsule"],             controlled:false, schedule:null    },
  { name:"Cyclobenzaprine",              strengths:["5mg","10mg"],               forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Methocarbamol",               strengths:["500mg","750mg"],            forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Methylprednisolone",           strengths:["4mg dose pack"],            forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Prednisone",                   strengths:["10mg","20mg","40mg"],       forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Ondansetron",                  strengths:["4mg","8mg"],               forms:["tablet","ODT"],       controlled:false, schedule:null    },
  { name:"Promethazine",                 strengths:["12.5mg","25mg"],           forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Acetaminophen",               strengths:["325mg","500mg","650mg"],    forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Alendronate",                  strengths:["35mg","70mg"],             forms:["tablet"],             controlled:false, schedule:null    },
  { name:"Ketorolac",                   strengths:["10mg","30mg"],             forms:["tablet"],             controlled:false, schedule:null    },
];

// ─── MEDICATION ORDER SETS ─────────────────────────────────────────────────────
const ORDER_SETS = [
  {
    category: "Post-op Pain Management",
    color: C.red,
    bg: C.redLt,
    sets: [
      { id:"carpal-tunnel", name:"Carpal Tunnel Release Pack", meds:[
        { drug_name:"Oxycodone",     strength:"5mg",    dosage_form:"tablet",  sig:"1 tab q4-6h PRN pain (max 6/day)", quantity:10, days_supply:2,  refills:0, is_controlled:true,  schedule:"C-II"  },
        { drug_name:"Acetaminophen", strength:"500mg",  dosage_form:"tablet",  sig:"1 tab q6h PRN pain",               quantity:40, days_supply:10, refills:2, is_controlled:false, schedule:null    },
        { drug_name:"Meloxicam",     strength:"15mg",   dosage_form:"tablet",  sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:3, is_controlled:false, schedule:null    },
      ]},
      { id:"shoulder-arthroscopy", name:"Shoulder Arthroscopy Pack", meds:[
        { drug_name:"Oxycodone",    strength:"5mg",  dosage_form:"tablet",  sig:"1 tab q4-6h PRN pain (max 6/day)", quantity:20, days_supply:4, refills:0, is_controlled:true,  schedule:"C-II"  },
        { drug_name:"Meloxicam",    strength:"15mg", dosage_form:"tablet",  sig:"1 tab daily with food",            quantity:30, days_supply:30,refills:3, is_controlled:false, schedule:null    },
        { drug_name:"Ondansetron",  strength:"4mg",  dosage_form:"ODT",     sig:"1 tab q8h PRN nausea",             quantity:15, days_supply:5, refills:0, is_controlled:false, schedule:null    },
        { drug_name:"Diazepam",     strength:"5mg",  dosage_form:"tablet",  sig:"1 tab q8h PRN muscle spasm",       quantity:10, days_supply:3, refills:0, is_controlled:true,  schedule:"C-IV"  },
      ]},
      { id:"orif-fracture", name:"ORIF / Fracture Pack", meds:[
        { drug_name:"Oxycodone",     strength:"5mg",   dosage_form:"tablet", sig:"1 tab q4-6h PRN pain (max 6/day)", quantity:30, days_supply:5,  refills:0, is_controlled:true,  schedule:"C-II"  },
        { drug_name:"Acetaminophen", strength:"500mg", dosage_form:"tablet", sig:"1 tab q6h PRN pain",               quantity:60, days_supply:15, refills:2, is_controlled:false, schedule:null    },
        { drug_name:"Meloxicam",     strength:"15mg",  dosage_form:"tablet", sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:3, is_controlled:false, schedule:null    },
        { drug_name:"Ondansetron",   strength:"4mg",   dosage_form:"ODT",    sig:"1 tab q8h PRN nausea",             quantity:10, days_supply:3,  refills:0, is_controlled:false, schedule:null    },
      ]},
      { id:"hand-wrist", name:"Hand / Wrist Surgery Pack", meds:[
        { drug_name:"Oxycodone",     strength:"5mg",   dosage_form:"tablet", sig:"1 tab q4-6h PRN pain (max 6/day)", quantity:15, days_supply:3,  refills:0, is_controlled:true,  schedule:"C-II"  },
        { drug_name:"Acetaminophen", strength:"500mg", dosage_form:"tablet", sig:"1 tab q6h PRN pain",               quantity:40, days_supply:10, refills:2, is_controlled:false, schedule:null    },
        { drug_name:"Meloxicam",     strength:"15mg",  dosage_form:"tablet", sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:3, is_controlled:false, schedule:null    },
      ]},
      { id:"total-joint", name:"Total Joint Pack", meds:[
        { drug_name:"Oxycodone",     strength:"5mg",   dosage_form:"tablet",  sig:"1 tab q4-6h PRN pain (max 6/day)", quantity:30, days_supply:5,  refills:0, is_controlled:true,  schedule:"C-II"  },
        { drug_name:"Meloxicam",     strength:"15mg",  dosage_form:"tablet",  sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:3, is_controlled:false, schedule:null    },
        { drug_name:"Aspirin",       strength:"81mg",  dosage_form:"tablet",  sig:"1 tab daily (DVT prophylaxis)",    quantity:60, days_supply:60, refills:3, is_controlled:false, schedule:null    },
        { drug_name:"Ondansetron",   strength:"4mg",   dosage_form:"ODT",     sig:"1 tab q8h PRN nausea",             quantity:15, days_supply:5,  refills:0, is_controlled:false, schedule:null    },
        { drug_name:"Docusate",      strength:"100mg", dosage_form:"capsule", sig:"1 cap BID",                        quantity:30, days_supply:15, refills:2, is_controlled:false, schedule:null    },
      ]},
      { id:"injection-visit", name:"Injection Visit Pack", meds:[
        { drug_name:"Meloxicam",     strength:"15mg",  dosage_form:"tablet", sig:"1 tab daily with food",  quantity:30, days_supply:30, refills:5, is_controlled:false, schedule:null },
        { drug_name:"Acetaminophen", strength:"500mg", dosage_form:"tablet", sig:"1 tab q6h PRN pain",     quantity:30, days_supply:7,  refills:2, is_controlled:false, schedule:null },
      ]},
    ],
  },
  {
    category: "Clinic Management",
    color: C.teal,
    bg: C.tealLt,
    sets: [
      { id:"anti-inflammatory", name:"Anti-inflammatory Pack", meds:[
        { drug_name:"Meloxicam",    strength:"15mg", dosage_form:"tablet",  sig:"1 tab daily with food",     quantity:30, days_supply:30, refills:5, is_controlled:false, schedule:null },
        { drug_name:"Omeprazole",   strength:"20mg", dosage_form:"capsule", sig:"1 cap daily (GI protection)",quantity:30, days_supply:30, refills:5, is_controlled:false, schedule:null },
      ]},
      { id:"muscle-relaxant", name:"Muscle Relaxant Pack", meds:[
        { drug_name:"Cyclobenzaprine", strength:"10mg",  dosage_form:"tablet", sig:"1 tab TID PRN muscle spasm", quantity:30, days_supply:10, refills:0, is_controlled:false, schedule:null },
        { drug_name:"Methocarbamol",   strength:"750mg", dosage_form:"tablet", sig:"1 tab QID PRN",              quantity:40, days_supply:10, refills:1, is_controlled:false, schedule:null },
      ]},
      { id:"neuropathic", name:"Neuropathic Pain Pack", meds:[
        { drug_name:"Gabapentin",   strength:"300mg", dosage_form:"capsule", sig:"1 cap TID",   quantity:90, days_supply:30, refills:5, is_controlled:true,  schedule:"C-V"  },
        { drug_name:"Duloxetine",   strength:"30mg",  dosage_form:"capsule", sig:"1 cap daily", quantity:30, days_supply:30, refills:5, is_controlled:false, schedule:null   },
      ]},
      { id:"topical", name:"Topical Pack", meds:[
        { drug_name:"Diclofenac",  strength:"1% gel",   dosage_form:"gel",   sig:"Apply TID to affected area",    quantity:100, days_supply:30, refills:3, is_controlled:false, schedule:null },
        { drug_name:"Lidocaine",   strength:"4% patch", dosage_form:"patch", sig:"Apply daily, up to 12 hours",   quantity:30,  days_supply:30, refills:3, is_controlled:false, schedule:null },
      ]},
    ],
  },
];

// ─── PRESCRIPTION TEMPLATES ───────────────────────────────────────────────────
const TEMPLATES = [
  { category:"Post-op Pain", color:C.red, bg:C.redLt, items:[
    { drug_name:"Oxycodone",                 strength:"5mg",         dosage_form:"tablet", sig:"1 tab q4-6h PRN pain (max 6/day)",   quantity:30, days_supply:5,  refills:0, is_controlled:true,  schedule:"C-II"  },
    { drug_name:"Hydrocodone/Acetaminophen", strength:"5/325mg",     dosage_form:"tablet", sig:"1 tab q6h PRN pain",                 quantity:20, days_supply:5,  refills:0, is_controlled:true,  schedule:"C-III" },
    { drug_name:"Tramadol",                  strength:"50mg",        dosage_form:"tablet", sig:"1 tab q6h PRN pain",                 quantity:30, days_supply:7,  refills:2, is_controlled:true,  schedule:"C-IV"  },
  ]},
  { category:"Anti-inflammatory", color:C.amber, bg:C.amberLt, items:[
    { drug_name:"Meloxicam",                strength:"15mg",         dosage_form:"tablet", sig:"1 tab daily with food",              quantity:30, days_supply:30, refills:5, is_controlled:false, schedule:null    },
    { drug_name:"Naproxen",                 strength:"500mg",        dosage_form:"tablet", sig:"1 tab BID with food",               quantity:60, days_supply:30, refills:3, is_controlled:false, schedule:null    },
    { drug_name:"Diclofenac",               strength:"75mg",         dosage_form:"tablet", sig:"1 tab BID",                         quantity:60, days_supply:30, refills:3, is_controlled:false, schedule:null    },
    { drug_name:"Methylprednisolone",       strength:"4mg dose pack",dosage_form:"tablet", sig:"as directed",                       quantity:1,  days_supply:6,  refills:0, is_controlled:false, schedule:null    },
  ]},
  { category:"Muscle Relaxant", color:C.teal, bg:C.tealLt, items:[
    { drug_name:"Cyclobenzaprine",          strength:"10mg",         dosage_form:"tablet", sig:"1 tab TID PRN muscle spasm",         quantity:30, days_supply:10, refills:2, is_controlled:false, schedule:null    },
    { drug_name:"Methocarbamol",           strength:"750mg",         dosage_form:"tablet", sig:"1 tab QID PRN",                     quantity:40, days_supply:10, refills:2, is_controlled:false, schedule:null    },
  ]},
  { category:"Post-op Nausea", color:C.teal, bg:C.tealLt, items:[
    { drug_name:"Ondansetron",              strength:"4mg",          dosage_form:"tablet", sig:"1 tab q8h PRN nausea",              quantity:15, days_supply:5,  refills:0, is_controlled:false, schedule:null    },
    { drug_name:"Promethazine",             strength:"25mg",         dosage_form:"tablet", sig:"1 tab q6h PRN nausea",              quantity:12, days_supply:3,  refills:0, is_controlled:false, schedule:null    },
  ]},
];

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const dAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };
const TODAY = todayISO();

export const MOCK_PRESCRIPTIONS = [
  { id:"RX001A", patient_id:"P001", drug_name:"Oxycodone",             generic_name:"oxycodone HCl",     strength:"5mg",       dosage_form:"tablet", sig:"1 tab q4-6h PRN pain, max 6/day",  quantity:30, days_supply:5,  refills:0, refills_remaining:0, is_controlled:true,  schedule:"C-II",  dea_number:"RW1234563", status:"active",       prescribed_date:dAgo(7),  last_filled:dAgo(7),  provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"M75.121", pdmp_checked:true,  allergy_checked:true },
  { id:"RX001B", patient_id:"P001", drug_name:"Meloxicam",              generic_name:"meloxicam",         strength:"15mg",      dosage_form:"tablet", sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:5, refills_remaining:4, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(30), last_filled:dAgo(30), provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"M75.121", pdmp_checked:false, allergy_checked:true },
  { id:"RX001C", patient_id:"P001", drug_name:"Cyclobenzaprine",        generic_name:"cyclobenzaprine",   strength:"10mg",      dosage_form:"tablet", sig:"1 tab TID PRN muscle spasm",       quantity:30, days_supply:10, refills:2, refills_remaining:1, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(14), last_filled:dAgo(14), provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"M75.121", pdmp_checked:false, allergy_checked:true },
  { id:"RX001H1",patient_id:"P001", drug_name:"Tramadol",               generic_name:"tramadol HCl",      strength:"50mg",      dosage_form:"tablet", sig:"1 tab q6h PRN pain",               quantity:30, days_supply:7,  refills:0, refills_remaining:0, is_controlled:true,  schedule:"C-IV",  dea_number:"RW1234563", status:"completed",    prescribed_date:dAgo(60), last_filled:dAgo(60), provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"M75.121", pdmp_checked:true,  allergy_checked:true },
  { id:"RX001H2",patient_id:"P001", drug_name:"Naproxen",               generic_name:"naproxen",          strength:"500mg",     dosage_form:"tablet", sig:"1 tab BID with food",              quantity:60, days_supply:30, refills:3, refills_remaining:0, is_controlled:false, schedule:null,    dea_number:null,        status:"discontinued", prescribed_date:dAgo(90), last_filled:dAgo(90), provider:"Dr. Walker", pharmacy_name:"CVS #1198",      pharmacy_fax:"480-555-7721", diagnosis_code:"M75.11",  pdmp_checked:false, allergy_checked:true },
  { id:"RX002A", patient_id:"P002", drug_name:"Hydrocodone/Acetaminophen",generic_name:"hydrocodone/APAP",strength:"5/325mg",  dosage_form:"tablet", sig:"1 tab q6h PRN pain",               quantity:20, days_supply:5,  refills:0, refills_remaining:0, is_controlled:true,  schedule:"C-III", dea_number:"RW1234563", status:"active",       prescribed_date:dAgo(10), last_filled:dAgo(10), provider:"Dr. Walker", pharmacy_name:"CVS #1198",      pharmacy_fax:"480-555-7721", diagnosis_code:"S52.501A",pdmp_checked:true,  allergy_checked:true },
  { id:"RX002B", patient_id:"P002", drug_name:"Naproxen",               generic_name:"naproxen",          strength:"500mg",     dosage_form:"tablet", sig:"1 tab BID with food",              quantity:60, days_supply:30, refills:3, refills_remaining:2, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(45), last_filled:dAgo(45), provider:"Dr. Walker", pharmacy_name:"CVS #1198",      pharmacy_fax:"480-555-7721", diagnosis_code:"S52.501A",pdmp_checked:false, allergy_checked:true },
  { id:"RX002C", patient_id:"P002", drug_name:"Ondansetron",            generic_name:"ondansetron HCl",   strength:"4mg",       dosage_form:"ODT",    sig:"1 tab q8h PRN nausea x 5 days",   quantity:15, days_supply:5,  refills:0, refills_remaining:0, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(10), last_filled:dAgo(10), provider:"Dr. Walker", pharmacy_name:"CVS #1198",      pharmacy_fax:"480-555-7721", diagnosis_code:"S52.501A",pdmp_checked:false, allergy_checked:true },
  { id:"RX003A", patient_id:"P003", drug_name:"Meloxicam",              generic_name:"meloxicam",         strength:"15mg",      dosage_form:"tablet", sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:5, refills_remaining:3, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(60), last_filled:dAgo(30), provider:"Dr. Walker", pharmacy_name:"Fry's Pharmacy",  pharmacy_fax:"480-555-3301", diagnosis_code:"M65.341", pdmp_checked:false, allergy_checked:true },
  { id:"RX003B", patient_id:"P003", drug_name:"Methocarbamol",         generic_name:"methocarbamol",     strength:"750mg",     dosage_form:"tablet", sig:"1 tab QID PRN spasm x 7 days",    quantity:28, days_supply:7,  refills:2, refills_remaining:1, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(14), last_filled:dAgo(14), provider:"Dr. Walker", pharmacy_name:"Fry's Pharmacy",  pharmacy_fax:"480-555-3301", diagnosis_code:"M65.341", pdmp_checked:false, allergy_checked:true },
  { id:"RX003C", patient_id:"P003", drug_name:"Tramadol",               generic_name:"tramadol HCl",      strength:"50mg",      dosage_form:"tablet", sig:"1 tab q6h PRN pain",               quantity:30, days_supply:7,  refills:2, refills_remaining:2, is_controlled:true,  schedule:"C-IV",  dea_number:"RW1234563", status:"active",       prescribed_date:dAgo(20), last_filled:dAgo(20), provider:"Dr. Walker", pharmacy_name:"Fry's Pharmacy",  pharmacy_fax:"480-555-3301", diagnosis_code:"M65.341", pdmp_checked:true,  allergy_checked:true },
  { id:"RX004A", patient_id:"P004", drug_name:"Tramadol",               generic_name:"tramadol HCl",      strength:"50mg",      dosage_form:"tablet", sig:"1 tab q8h PRN pain",               quantity:30, days_supply:10, refills:2, refills_remaining:1, is_controlled:true,  schedule:"C-IV",  dea_number:"RW1234563", status:"active",       prescribed_date:dAgo(25), last_filled:dAgo(25), provider:"Dr. Walker", pharmacy_name:"Banner Pharmacy", pharmacy_fax:"602-555-8811", diagnosis_code:"M18.11",  pdmp_checked:true,  allergy_checked:true },
  { id:"RX004B", patient_id:"P004", drug_name:"Alendronate",            generic_name:"alendronate sodium","strength":"70mg",    dosage_form:"tablet", sig:"1 tab weekly on same day each week",quantity:4, days_supply:28, refills:11,refills_remaining:10,is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(90), last_filled:dAgo(28), provider:"Dr. Walker", pharmacy_name:"Banner Pharmacy", pharmacy_fax:"602-555-8811", diagnosis_code:"M81.0",   pdmp_checked:false, allergy_checked:true },
  { id:"RX004C", patient_id:"P004", drug_name:"Acetaminophen",          generic_name:"acetaminophen",     strength:"500mg",     dosage_form:"tablet", sig:"1-2 tabs q6h PRN pain (max 4g/day)",quantity:60,days_supply:15, refills:3, refills_remaining:2, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(40), last_filled:dAgo(40), provider:"Dr. Walker", pharmacy_name:"Banner Pharmacy", pharmacy_fax:"602-555-8811", diagnosis_code:"M18.11",  pdmp_checked:false, allergy_checked:true },
  { id:"RX004H1",patient_id:"P004", drug_name:"Methylprednisolone",     generic_name:"methylprednisolone","strength":"4mg dose pack",dosage_form:"tablet",sig:"as directed",               quantity:1, days_supply:6,  refills:0, refills_remaining:0, is_controlled:false, schedule:null,    dea_number:null,        status:"completed",    prescribed_date:dAgo(50), last_filled:dAgo(50), provider:"Dr. Walker", pharmacy_name:"Banner Pharmacy", pharmacy_fax:"602-555-8811", diagnosis_code:"M18.11",  pdmp_checked:false, allergy_checked:true },
  { id:"RX005A", patient_id:"P005", drug_name:"Meloxicam",              generic_name:"meloxicam",         strength:"15mg",      dosage_form:"tablet", sig:"1 tab daily with food",            quantity:30, days_supply:30, refills:5, refills_remaining:4, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(38), last_filled:dAgo(38), provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"S63.641A",pdmp_checked:false, allergy_checked:true },
  { id:"RX005B", patient_id:"P005", drug_name:"Cyclobenzaprine",        generic_name:"cyclobenzaprine",   strength:"10mg",      dosage_form:"tablet", sig:"1 tab TID PRN spasm",              quantity:30, days_supply:10, refills:2, refills_remaining:1, is_controlled:false, schedule:null,    dea_number:null,        status:"active",       prescribed_date:dAgo(38), last_filled:dAgo(38), provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"S63.641A",pdmp_checked:false, allergy_checked:true },
  { id:"RX005C", patient_id:"P005", drug_name:"Ondansetron",            generic_name:"ondansetron HCl",   strength:"4mg",       dosage_form:"ODT",    sig:"1 tab q8h PRN nausea x 3 days",   quantity:9,  days_supply:3,  refills:0, refills_remaining:0, is_controlled:false, schedule:null,    dea_number:null,        status:"completed",    prescribed_date:dAgo(35), last_filled:dAgo(35), provider:"Dr. Walker", pharmacy_name:"Walgreens #4421", pharmacy_fax:"480-555-9901", diagnosis_code:"S63.641A",pdmp_checked:false, allergy_checked:true },
];

export const MOCK_ALLERGIES = {
  P001: [{ id:"AL001A", patient_id:"P001", allergen:"Penicillin",   reaction:"Skin rash, urticaria",             severity:"Moderate",        onset_date:"1995-01-01" }],
  P002: [],
  P003: [
    { id:"AL003A", patient_id:"P003", allergen:"Sulfonamides",  reaction:"Stevens-Johnson Syndrome",          severity:"Life-threatening", onset_date:"2010-06-15" },
    { id:"AL003B", patient_id:"P003", allergen:"Codeine",       reaction:"Respiratory depression, apnea",     severity:"Severe",           onset_date:"2018-03-20" },
  ],
  P004: [{ id:"AL004A", patient_id:"P004", allergen:"NSAIDs",       reaction:"GI bleeding, peptic ulceration", severity:"Severe",           onset_date:"2019-08-10" }],
  P005: [],
};

export const MOCK_REFILL_REQUESTS = [
  { id:"RFQ001", patient_id:"P001", patient_name:"James Hartley",   drug_name:"Oxycodone 5mg",             pharmacy:"Walgreens #4421",  requested_date:dAgo(1), days_since_last_fill:6,  rx_id:"RX001A", is_controlled:true,  schedule:"C-II" },
  { id:"RFQ002", patient_id:"P003", patient_name:"Robert Chen",     drug_name:"Meloxicam 15mg",            pharmacy:"Fry's Pharmacy",   requested_date:dAgo(2), days_since_last_fill:28, rx_id:"RX003A", is_controlled:false, schedule:null   },
  { id:"RFQ003", patient_id:"P004", patient_name:"Patricia Monroe", drug_name:"Tramadol 50mg",             pharmacy:"Banner Pharmacy",  requested_date:dAgo(1), days_since_last_fill:24, rx_id:"RX004A", is_controlled:true,  schedule:"C-IV" },
  { id:"RFQ004", patient_id:"P005", patient_name:"David Okafor",    drug_name:"Meloxicam 15mg",            pharmacy:"Walgreens #4421",  requested_date:TODAY,   days_since_last_fill:37, rx_id:"RX005A", is_controlled:false, schedule:null   },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const severityColor = { "Mild":"badge-green", "Moderate":"badge-amber", "Severe":"badge-red", "Life-threatening":"badge-red" };
const severityBorder = { "Mild":C.green, "Moderate":C.amber, "Severe":C.red, "Life-threatening":C.red };

const scheduleColor = (s) => {
  if (s === "C-II")  return { color: "#ff4444", bg: "rgba(255,68,68,0.15)" };
  if (s === "C-III") return { color: C.red,     bg: C.redLt };
  if (s === "C-IV")  return { color: C.amber,   bg: C.amberLt };
  if (s === "C-V")   return { color: C.amber,   bg: "rgba(240,165,0,0.08)" };
  return { color: C.text3, bg: "transparent" };
};

const rxStatusColor = (rx) => {
  if (rx.status !== "active") return C.text3;
  if (rx.refills_remaining === 0 && rx.is_controlled) return C.red;
  if (rx.refills_remaining === 0) return C.amber;
  return C.green;
};

// ─── ALLERGY ALERT MODAL ──────────────────────────────────────────────────────
function AllergyAlertModal({ allergen, reaction, onOverride, onCancel }) {
  const [reason, setReason] = useState("");
  return (
    <div className="cmd-overlay" style={{ zIndex: 300 }}>
      <div className="cmd-modal slide-in" style={{ width: 480, border: `2px solid ${C.red}` }}>
        <div style={{ padding: "16px 20px", background: C.redLt, borderBottom: `1px solid ${C.red}30`, display: "flex", gap: 10 }}>
          <span style={{ fontSize: 22, color: C.red }}>⚠</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.red }}>ALLERGY ALERT</div>
            <div style={{ fontSize: 12, color: C.text2 }}>Patient has a documented drug allergy</div>
          </div>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ padding: "12px 16px", background: C.navy3, borderRadius: 8, borderLeft: `3px solid ${C.red}` }}>
            <div style={{ fontSize: 13, color: C.text1 }}>
              <strong style={{ color: C.red }}>Allergen:</strong> {allergen}
            </div>
            <div style={{ fontSize: 13, color: C.text1, marginTop: 4 }}>
              <strong>Reaction:</strong> {reaction}
            </div>
          </div>
          <div style={{ fontSize: 13, color: C.text2 }}>
            This drug may cross-react with the documented allergy. Do you want to override and proceed?
          </div>
          <div className="field">
            <div className="field-label" style={{ color: C.red }}>Override reason (required)</div>
            <input className="field-input" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Clinical justification for override…" />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" style={{ background: C.red, borderColor: C.red }}
              disabled={!reason.trim()} onClick={() => onOverride(reason)}>
              Override & Prescribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── NO ALLERGY WARNING ───────────────────────────────────────────────────────
function NoAllergyModal({ onConfirm, onCancel }) {
  return (
    <div className="cmd-overlay" style={{ zIndex: 300 }}>
      <div className="cmd-modal slide-in" style={{ width: 440 }}>
        <div style={{ padding: "16px 20px", background: C.amberLt, borderBottom: `1px solid ${C.amber}30` }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: C.amber }}>⚠ No Allergies Documented</div>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13, color: C.text2 }}>
            No allergies are on file for this patient. Please verify allergy status before prescribing.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={onCancel}>Go Back</button>
            <button className="btn btn-primary" style={{ background: C.amber, borderColor: C.amber }} onClick={onConfirm}>
              Confirm No Allergies & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FAX MODAL ────────────────────────────────────────────────────────────────
function FaxModal({ rx, onClose }) {
  const isC2 = rx.schedule === "C-II";
  const confNum = `FAX-${Math.random().toString(36).substr(2,8).toUpperCase()}`;
  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-modal slide-in" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
          <span style={{ fontWeight: 500 }}>Fax to Pharmacy</span>
          <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          {isC2 ? (
            <div className="notification notification-warn">
              <span>⚠</span>
              <span>Electronic faxing is not available for Schedule C-II substances. Please print the prescription and hand-deliver or use DEA-compliant certified e-prescribing software (EPCS).</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="notification notification-info"><span>✓</span><span>Prescription sent successfully</span></div>
              <div style={{ padding: "12px 16px", background: C.navy3, borderRadius: 8, display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                {[["Pharmacy", rx.pharmacy_name], ["Fax", rx.pharmacy_fax], ["Drug", `${rx.drug_name} ${rx.strength}`], ["Confirmation", confNum]].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", gap: 10 }}>
                    <span style={{ color: C.text3, width: 90 }}>{l}</span>
                    <span style={{ color: l === "Confirmation" ? C.teal : C.text1, fontFamily: l === "Confirmation" ? "var(--mono)" : "inherit" }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button className="btn btn-secondary btn-sm mt-8" style={{ marginTop: 12 }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── DENY REFILL MODAL ────────────────────────────────────────────────────────
function DenyRefillModal({ request, onDeny, onClose }) {
  const [reason, setReason] = useState("");
  const REASONS = ["Too soon to refill", "Controlled substance policy", "Needs office visit first", "Discontinue medication", "Patient non-compliant", "Quantity mismatch"];
  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-modal slide-in" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: 500 }}>Deny Refill Request</div>
          <div style={{ fontSize: 11, color: C.text3 }}>{request.patient_name} — {request.drug_name}</div>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="field">
            <div className="field-label">Reason for denial</div>
            <select className="field-input field-select" value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">Select reason…</option>
              {REASONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ background: C.red, borderColor: C.red }}
              disabled={!reason} onClick={() => { onDeny(reason); onClose(); }}>
              Deny Refill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PRINT RX ─────────────────────────────────────────────────────────────────
const printRx = (rx, patient) => {
  const win = window.open("", "_blank", "width=600,height=800");
  win.document.write(`<!DOCTYPE html><html><head><title>Prescription</title><style>
    body{font-family:Arial,sans-serif;max-width:480px;margin:32px auto;font-size:13px;line-height:1.6;color:#000}
    .watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-45deg);font-size:36px;color:rgba(0,0,0,0.08);white-space:nowrap;font-weight:bold;pointer-events:none}
    .practice{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:12px}
    .practice-name{font-size:16px;font-weight:bold}
    .section{margin:8px 0}
    .label{font-weight:bold}
    .drug-box{border:1px solid #000;padding:12px;margin:14px 0;background:#fafafa}
    .sig{font-size:14px;font-weight:bold;margin:6px 0}
    .sig-line{margin-top:32px;border-top:1px solid #000;padding-top:4px}
    .footer{font-size:10px;color:#666;margin-top:16px;text-align:center}
    @media print{.no-print{display:none}}
  </style></head><body>
  ${rx.is_controlled && rx.schedule === "C-II" ? '<div class="watermark">VOID IF NOT ON SECURITY PAPER</div>' : ''}
  <div class="practice">
    <div class="practice-name">Epiphany MD Medical Group</div>
    <div>1234 N Medical Plaza Dr, Scottsdale, AZ 85251</div>
    <div>Phone: (480) 555-0100 · Fax: (480) 555-0101</div>
    <div>Provider: ${rx.provider} ${rx.dea_number ? `· DEA: ${rx.dea_number}` : ''}</div>
  </div>
  <div class="section"><span class="label">Patient: </span>${patient?.first || ""} ${patient?.last || ""}</div>
  <div class="section"><span class="label">DOB: </span>${patient?.dob || "—"}</div>
  <div class="section"><span class="label">Date: </span>${rx.prescribed_date || "${TODAY}"}</div>
  <div class="drug-box">
    <div style="font-size:16px;font-weight:bold">${rx.drug_name} ${rx.strength}</div>
    <div>${rx.dosage_form}</div>
    <div class="sig">Sig: ${rx.sig}</div>
    <div>Quantity: #${rx.quantity} (${rx.quantity} ${rx.dosage_form}${rx.quantity !== 1 ? "s" : ""})</div>
    <div>Refills: ${rx.refills} ${rx.is_controlled ? "(controlled substance)" : ""}</div>
    ${rx.is_controlled ? `<div style="color:red;font-weight:bold">Schedule ${rx.schedule}</div>` : ""}
  </div>
  <div class="section"><span class="label">Diagnosis: </span>${rx.diagnosis_code || "—"}</div>
  <div class="section"><span class="label">Pharmacy: </span>${rx.pharmacy_name || "—"}</div>
  <div class="sig-line">
    <span class="label">Provider Signature: </span>_______________________________
  </div>
  <div class="footer">
    This prescription is valid for 1 year from the date written.${rx.is_controlled ? " Controlled substance — federal law prohibits transfer." : ""}
  </div>
  <div class="no-print" style="margin-top:20px;text-align:center">
    <button onclick="window.print()" style="padding:8px 20px;font-size:13px;cursor:pointer">Print</button>
    <button onclick="window.close()" style="padding:8px 20px;font-size:13px;cursor:pointer;margin-left:8px">Close</button>
  </div>
  </body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 500);
};

// ─── ORDER SET CARD ───────────────────────────────────────────────────────────
function OrderSetCard({ set, catColor, patient, onSaved, onBatchOrdered }) {
  const [checked,      setChecked]    = useState(() => new Set(set.meds.map((_, i) => i)));
  const [customEdits,  setCustomEdits]= useState({});   // { [medIdx]: { sig, quantity, refills } }
  const [expandIdx,    setExpandIdx]  = useState(null); // which med has inline edit open
  const [ordering,     setOrdering]   = useState(false);
  const [orderedCount, setOrderedCount]=useState(null);

  const toggleCheck = (i) => setChecked(c => { const n = new Set(c); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const updateEdit  = (i, field, val) => setCustomEdits(e => ({ ...e, [i]: { ...(e[i] || {}), [field]: val } }));

  const TODAY_STR = todayISO();

  const handleOrder = async () => {
    const rows = set.meds
      .filter((_, i) => checked.has(i))
      .map((med, _) => {
        const origIdx = set.meds.indexOf(med);
        const edits = customEdits[origIdx] || {};
        const refills = edits.refills ?? med.refills ?? 0;
        return {
          drug_name:       med.drug_name,
          generic_name:    med.drug_name.toLowerCase(),
          strength:        med.strength,
          dosage_form:     med.dosage_form || "tablet",
          sig:             edits.sig      ?? med.sig,
          quantity:        edits.quantity ?? med.quantity,
          days_supply:     med.days_supply || 30,
          refills,
          refills_remaining: refills,
          is_controlled:   !!med.is_controlled,
          schedule:        med.schedule || "",
          dea_number:      med.is_controlled ? "RW1234563" : "",
          diagnosis_code:  "",
          pharmacy_name:   "",
          pharmacy_fax:    "",
          notes:           "",
          patient_id:      patient.id,
          prescribed_date: TODAY_STR,
          status:          "active",
          allergy_checked: true,
          pdmp_checked:    false,
          provider:        "Dr. Walker",
        };
      });
    if (!rows.length) return;
    setOrdering(true);
    try {
      const { data, error } = await db.from("prescriptions").insert(rows).select();
      if (error) console.error("Order set insert error:", error);
      const inserted = data?.length ? data : rows;
      inserted.forEach(row => onSaved && onSaved(row));
      setOrderedCount(inserted.length);
      if (onBatchOrdered) onBatchOrdered(inserted.length);
      setTimeout(() => setOrderedCount(null), 4000);
    } catch (err) { console.error(err); }
    setOrdering(false);
  };

  return (
    <div style={{ background: C.navy3, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 10 }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text1 }}>{set.name}</div>
          <div style={{ fontSize: 11, color: C.text3, marginTop: 1 }}>{set.meds.length} medications · {checked.size} selected</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {orderedCount !== null && <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>✓ {orderedCount} ordered</span>}
          <button
            onClick={handleOrder}
            disabled={ordering || checked.size === 0}
            style={{ padding: "5px 12px", borderRadius: 5, background: checked.size === 0 ? C.navy4 : catColor, border: "none", color: checked.size === 0 ? C.text3 : "#fff", fontSize: 11, fontWeight: 600, cursor: checked.size === 0 ? "not-allowed" : "pointer" }}
          >
            {ordering ? "Ordering…" : `✅ Order ${checked.size}`}
          </button>
        </div>
      </div>

      <div style={{ padding: "6px 14px 10px" }}>
        {set.meds.map((med, i) => (
          <div key={i}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < set.meds.length - 1 && expandIdx !== i ? `1px solid ${C.border}` : "none" }}>
              <input type="checkbox" checked={checked.has(i)} onChange={() => toggleCheck(i)}
                style={{ accentColor: catColor, cursor: "pointer", flexShrink: 0, width: 14, height: 14 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: checked.has(i) ? C.text1 : C.text3 }}>
                  {med.drug_name} {customEdits[i]?.strength || med.strength}
                  {med.is_controlled && <span style={{ marginLeft: 5, fontSize: 10, background: "#7c3aed22", color: "#a78bfa", padding: "1px 5px", borderRadius: 3, fontWeight: 600 }}>{med.schedule}</span>}
                </div>
                <div style={{ fontSize: 10, color: C.text3 }}>
                  {customEdits[i]?.sig || med.sig} · #{customEdits[i]?.quantity || med.quantity}
                  {(customEdits[i]?.refills ?? med.refills) > 0 ? ` · ${customEdits[i]?.refills ?? med.refills} RF` : ""}
                </div>
              </div>
              <button onClick={() => setExpandIdx(expandIdx === i ? null : i)}
                style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "transparent", border: `1px solid ${C.border2}`, color: C.text2, cursor: "pointer", flexShrink: 0 }}>
                {expandIdx === i ? "▲ Done" : "✏️ Edit"}
              </button>
            </div>

            {expandIdx === i && (
              <div style={{ background: C.navy4, borderRadius: 6, padding: "10px 12px", margin: "2px 0 8px", border: `1px solid ${C.border2}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px", gap: 8 }}>
                  <label style={{ fontSize: 11, color: C.text3 }}>
                    Directions (Sig)
                    <input value={customEdits[i]?.sig ?? med.sig} onChange={e => updateEdit(i, "sig", e.target.value)}
                      style={{ display: "block", width: "100%", marginTop: 3, padding: "5px 8px", background: C.navy3, border: `1px solid ${C.border2}`, borderRadius: 4, color: C.text1, fontSize: 11 }} />
                  </label>
                  <label style={{ fontSize: 11, color: C.text3 }}>
                    Qty
                    <input type="number" value={customEdits[i]?.quantity ?? med.quantity} onChange={e => updateEdit(i, "quantity", e.target.value)}
                      style={{ display: "block", width: "100%", marginTop: 3, padding: "5px 8px", background: C.navy3, border: `1px solid ${C.border2}`, borderRadius: 4, color: C.text1, fontSize: 11 }} />
                  </label>
                  <label style={{ fontSize: 11, color: C.text3 }}>
                    Refills
                    <input type="number" min={0} max={med.schedule === "C-II" ? 0 : 12} value={customEdits[i]?.refills ?? med.refills} onChange={e => updateEdit(i, "refills", Number(e.target.value))}
                      style={{ display: "block", width: "100%", marginTop: 3, padding: "5px 8px", background: C.navy3, border: `1px solid ${C.border2}`, borderRadius: 4, color: C.text1, fontSize: 11 }} />
                  </label>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MEDICATION ORDER SETS MODAL ──────────────────────────────────────────────
export function MedOrderSetsModal({ patient, allergies, onSaved, onClose }) {
  const [activeTab,     setActiveTab]    = useState("prebuilt");
  const [savedBatches,  setSavedBatches] = useState([]);
  const [loadingBatches,setLoadingBatches]=useState(false);
  const [batchName,     setBatchName]    = useState("");
  const [customMeds,    setCustomMeds]   = useState([]);
  const [drugQuery,     setDrugQuery]    = useState("");
  const [showDrugDrop,  setShowDrugDrop] = useState(false);
  const [savingBatch,   setSavingBatch]  = useState(false);
  const [savedMsg,      setSavedMsg]     = useState("");
  const [totalOrdered,  setTotalOrdered] = useState(0);

  const appCtx = useApp();

  useEffect(() => {
    if (activeTab === "saved") {
      setLoadingBatches(true);
      (async () => {
        try {
          const { data } = await db.from("medication_batches").select("id, batch_name, medications, created_at, practice_id").order("created_at", { ascending: false });
          setSavedBatches(data || []);
        } catch (err) { console.error("Batch fetch error:", err); }
        setLoadingBatches(false);
      })();
    }
  }, [activeTab]);

  const drugResults = showDrugDrop && drugQuery.length >= 1
    ? searchMedications(drugQuery, 6)
    : [];

  const addCustomMed = (med) => {
    setCustomMeds(m => [...m, {
      drug_name: med.name, strength: med.commonDoses[0] || "", dosage_form: med.dosageForms[0] || "tablet",
      sig: med.commonSigs[0] || "", quantity: med.defaultQuantity, days_supply: med.defaultDaysSupply,
      refills: med.isControlled && med.schedule === "C-II" ? 0 : med.defaultRefills,
      is_controlled: med.isControlled, schedule: med.schedule || null,
    }]);
    setDrugQuery("");
    setShowDrugDrop(false);
  };

  const updateCustomMed = (i, field, val) =>
    setCustomMeds(m => m.map((x, j) => j === i ? { ...x, [field]: val } : x));

  const handleSaveBatch = async () => {
    if (!batchName.trim() || !customMeds.length) return;
    setSavingBatch(true);
    try {
      const { email: _sessEmail } = await getCurrentUserInfo();
      const _fallbackPid = await getCurrentPracticeId();
      const { error } = await db.from("medication_batches").insert([{
        batch_name: batchName.trim(),
        medications: customMeds,
        provider: appCtx?.currentProvider ? `${appCtx.currentProvider.first_name || ''} ${appCtx.currentProvider.last_name || ''}`.trim() : (_sessEmail || 'Provider'),
        practice_id: appCtx?.practiceId || _fallbackPid,
      }]);
      if (error) console.error("Save batch error:", error);
      else {
        setSavedMsg("✓ Batch saved to library");
        setBatchName("");
        setCustomMeds([]);
        setTimeout(() => setSavedMsg(""), 3000);
      }
    } catch (err) { console.error(err); }
    setSavingBatch(false);
  };

  const TABS = [
    { id:"prebuilt", label:"Pre-built Sets" },
    { id:"saved",    label:"My Saved Batches" },
    { id:"custom",   label:"➕ Create Custom" },
  ];

  const inputStyle = { width:"100%", padding:"7px 10px", background:C.navy3, border:`1px solid ${C.border2}`, borderRadius:5, color:C.text1, fontSize:12, outline:"none" };

  return createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"32px 16px", overflowY:"auto" }}
      onClick={onClose}>
      <div style={{ width:"100%", maxWidth:740, background:C.navy2, borderRadius:12, border:`1px solid ${C.border2}`, display:"flex", flexDirection:"column", maxHeight:"calc(100vh - 64px)" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text1 }}>💊 Medication Order Sets</div>
            <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>Pre-built batches for {patient.name || `${patient.first_name || ""} ${patient.last_name || ""}`.trim()}</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            {totalOrdered > 0 && <span style={{ fontSize:12, color:C.green, fontWeight:600 }}>✓ {totalOrdered} Rx ordered this session</span>}
            <button onClick={onClose} style={{ background:"none", border:"none", color:C.text2, fontSize:18, cursor:"pointer", lineHeight:1 }}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding:"10px 18px", background:"none", border:"none",
              borderBottom:`2px solid ${activeTab === t.id ? C.teal : "transparent"}`,
              color: activeTab === t.id ? C.teal : C.text2,
              fontSize:13, cursor:"pointer", fontWeight: activeTab === t.id ? 600 : 400,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY:"auto", flex:1, padding:"16px 20px" }}>

          {/* ── Pre-built ─────────────────────────────────────────────────── */}
          {activeTab === "prebuilt" && ORDER_SETS.map(cat => (
            <div key={cat.category} style={{ marginBottom:24 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                <div style={{ height:1, flex:1, background:cat.color + "30" }} />
                <div style={{ fontSize:10, fontWeight:700, color:cat.color, textTransform:"uppercase", letterSpacing:"0.08em", whiteSpace:"nowrap" }}>{cat.category}</div>
                <div style={{ height:1, flex:1, background:cat.color + "30" }} />
              </div>
              {cat.sets.map(set => (
                <OrderSetCard key={set.id} set={set} catColor={cat.color} patient={patient} allergies={allergies} onSaved={onSaved} onBatchOrdered={n => setTotalOrdered(t => t + n)} />
              ))}
            </div>
          ))}

          {/* ── Saved Batches ─────────────────────────────────────────────── */}
          {activeTab === "saved" && (
            loadingBatches
              ? <div style={{ textAlign:"center", padding:40, color:C.text3 }}>Loading…</div>
              : savedBatches.length === 0
                ? <div style={{ textAlign:"center", padding:40, color:C.text3, fontSize:13 }}>No saved batches yet. Use "Create Custom" to build your own.</div>
                : savedBatches.map(b => (
                    <OrderSetCard key={b.id}
                      set={{ id:b.id, name:b.batch_name, meds: Array.isArray(b.medications) ? b.medications : [] }}
                      catColor={C.teal} patient={patient} allergies={allergies} onSaved={onSaved} onBatchOrdered={n => setTotalOrdered(t => t + n)} />
                  ))
          )}

          {/* ── Create Custom ─────────────────────────────────────────────── */}
          {activeTab === "custom" && (
            <div style={{ maxWidth:560 }}>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, color:C.text2, display:"block", marginBottom:5 }}>Batch Name</label>
                <input value={batchName} onChange={e => setBatchName(e.target.value)}
                  placeholder='e.g. "My Shoulder Scope Meds"'
                  style={inputStyle} />
              </div>

              {/* Drug search */}
              <div style={{ marginBottom:14, position:"relative" }}>
                <label style={{ fontSize:12, color:C.text2, display:"block", marginBottom:5 }}>Add Medication</label>
                <input value={drugQuery} onChange={e => { setDrugQuery(e.target.value); setShowDrugDrop(true); }}
                  onFocus={() => setShowDrugDrop(true)} onBlur={() => setTimeout(() => setShowDrugDrop(false), 150)}
                  placeholder="Search drug name…"
                  style={inputStyle} />
                {drugResults.length > 0 && (
                  <div style={{ position:"absolute", top:"100%", left:0, right:0, background:C.navy3, border:`1px solid ${C.border2}`, borderRadius:6, zIndex:10, marginTop:2, overflow:"hidden", maxHeight:200, overflowY:"auto" }}>
                    {drugResults.map(d => (
                      <div key={d.name} onMouseDown={() => addCustomMed(d)}
                        style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, color:C.text1, borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div>
                          <span>{d.name}</span>
                          {d.brandNames?.length > 0 && <span style={{ fontSize:11, color:C.text3, marginLeft:6 }}>({d.brandNames[0]})</span>}
                        </div>
                        {d.isControlled && <span style={{ fontSize:10, color:"#a78bfa", fontWeight:700 }}>⚠ {d.schedule}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Custom med list */}
              {customMeds.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, color:C.text3, marginBottom:8 }}>Medications in batch ({customMeds.length})</div>
                  {customMeds.map((med, i) => (
                    <div key={i} style={{ background:C.navy3, borderRadius:6, padding:"10px 12px", marginBottom:8, border:`1px solid ${C.border}` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <div style={{ fontSize:13, fontWeight:500, color:C.text1 }}>
                          {med.drug_name} {med.strength}
                          {med.is_controlled && <span style={{ marginLeft:6, fontSize:10, color:"#a78bfa" }}>{med.schedule}</span>}
                        </div>
                        <button onClick={() => setCustomMeds(m => m.filter((_, j) => j !== i))}
                          style={{ background:"none", border:"none", color:C.red, cursor:"pointer", fontSize:15 }}>✕</button>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 70px", gap:8 }}>
                        <label style={{ fontSize:11, color:C.text3 }}>
                          Sig (Directions)
                          <input value={med.sig} onChange={e => updateCustomMed(i, "sig", e.target.value)}
                            placeholder="e.g. 1 tab TID PRN"
                            style={{ display:"block", width:"100%", marginTop:3, padding:"4px 7px", background:C.navy4, border:`1px solid ${C.border2}`, borderRadius:4, color:C.text1, fontSize:11, outline:"none" }} />
                        </label>
                        <label style={{ fontSize:11, color:C.text3 }}>
                          Qty
                          <input type="number" value={med.quantity} onChange={e => updateCustomMed(i, "quantity", Number(e.target.value))}
                            style={{ display:"block", width:"100%", marginTop:3, padding:"4px 7px", background:C.navy4, border:`1px solid ${C.border2}`, borderRadius:4, color:C.text1, fontSize:11, outline:"none" }} />
                        </label>
                        <label style={{ fontSize:11, color:C.text3 }}>
                          Refills
                          <input type="number" min={0} max={med.schedule === "C-II" ? 0 : 12} value={med.refills} onChange={e => updateCustomMed(i, "refills", Number(e.target.value))}
                            style={{ display:"block", width:"100%", marginTop:3, padding:"4px 7px", background:C.navy4, border:`1px solid ${C.border2}`, borderRadius:4, color:C.text1, fontSize:11, outline:"none" }} />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {savedMsg && <div style={{ marginBottom:10, fontSize:12, color:C.green, fontWeight:600 }}>{savedMsg}</div>}

              <button onClick={handleSaveBatch}
                disabled={!batchName.trim() || !customMeds.length || savingBatch}
                className="btn btn-primary btn-sm" style={{ width:"100%" }}>
                {savingBatch ? "Saving…" : "💾 Save Batch to Library"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── ADD MEDICATION MODAL ─────────────────────────────────────────────────────
const MED_SOURCES = ["Patient reported", "Outside prescription", "Hospital discharge", "Other"];
const LABEL_ST = { fontSize: 11, color: C.text3, fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.07em' };
const EMPTY_ADD_MED = { drug_name: '', strength: '', sig: '', start_date: TODAY, provider: 'Dr. Walker', source: 'Patient reported', notes: '' };

function AddMedicationModal({ patient, onSaved, onClose }) {
  const [form, setForm] = useState(EMPTY_ADD_MED);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.drug_name.trim()) return;
    setSaving(true);
    setError('');
    const row = {
      patient_id:       patient.id,
      drug_name:        form.drug_name.trim(),
      strength:         form.strength,
      sig:              form.sig,
      prescribed_date:  form.start_date || TODAY,
      provider:         form.provider || 'Dr. Walker',
      source:           form.source,
      notes:            form.notes,
      status:           'active',
      quantity:         null,
      refills:          0,
      refills_remaining: 0,
      allergy_checked:  false,
      pdmp_checked:     false,
    };
    const { data, error: err } = await db.from('prescriptions').insert([row]).select().single();
    setSaving(false);
    if (err) { setError(err.message); return; }
    logAudit('create', 'medication', (data || row).id, (data || row).patient_id, { drug: row.drug_name, source: row.source });
    onSaved(data || row);
    onClose();
  };

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.navy2, border: `1px solid ${C.border2}`, borderRadius: 10, padding: 24, width: 'min(480px, 94vw)', boxShadow: '0 16px 48px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: C.text1 }}>Add Medication to Chart</div>
          <div style={{ fontSize: 12, color: C.text3, marginTop: 3 }}>Document a medication the patient is already taking — not a new prescription.</div>
        </div>

        {/* Drug name */}
        <MedSelect label="Drug name" value={form.drug_name} onChange={v => set('drug_name', v)} placeholder="Search or type medication name…" />

        {/* Dose / Strength + Frequency */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={LABEL_ST}>Dose / Strength</label>
            <input className="field-input" style={{ width: '100%', boxSizing: 'border-box' }}
              placeholder="e.g. 10mg" value={form.strength}
              onChange={e => set('strength', e.target.value)} />
          </div>
          <div style={{ flex: 2 }}>
            <label style={LABEL_ST}>Frequency / Sig</label>
            <input className="field-input" style={{ width: '100%', boxSizing: 'border-box' }}
              placeholder="e.g. Take 1 tablet by mouth twice daily"
              value={form.sig} onChange={e => set('sig', e.target.value)} />
          </div>
        </div>

        {/* Start date + Prescriber */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={LABEL_ST}>Start Date</label>
            <input type="date" className="field-input" style={{ width: '100%', boxSizing: 'border-box' }}
              value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={LABEL_ST}>Prescriber</label>
            <input className="field-input" style={{ width: '100%', boxSizing: 'border-box' }}
              placeholder="Dr. Walker" value={form.provider}
              onChange={e => set('provider', e.target.value)} />
          </div>
        </div>

        {/* Source */}
        <div>
          <label style={LABEL_ST}>Source</label>
          <select className="field-input field-select" style={{ width: '100%', boxSizing: 'border-box' }}
            value={form.source} onChange={e => set('source', e.target.value)}>
            {MED_SOURCES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* Notes */}
        <div>
          <label style={LABEL_ST}>Notes</label>
          <textarea className="field-input" style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 60 }}
            placeholder="Optional — e.g. taking since 2022, prescribed by PCP"
            value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        {error && <div style={{ fontSize: 12, color: C.red }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}
            disabled={!form.drug_name.trim() || saving}>
            {saving ? 'Saving…' : 'Add to Medications'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── LEFT PANEL ───────────────────────────────────────────────────────────────
const ALLERGY_TYPES = ["Drug", "Food", "Environmental", "Latex", "Contrast Dye", "Other"];
const BLANK_ALLERGY_FORM = { allergen: "", reaction: "", severity: "Mild", allergy_type: "Drug", onset_date: TODAY };

function LeftPanel({ patient, prescriptions, allergies, setAllergies, onNewRx, onOrderSet, onReorder, onAddAllergy, onDiscontinue, onDeleteRx, onMedAdded }) {
  const [showAddMed, setShowAddMed] = useState(false);
  const [allergyFormMode, setAllergyFormMode] = useState(null); // null | "add" | "edit"
  const [editingAllergy, setEditingAllergy] = useState(null);
  const [allergyForm, setAllergyForm] = useState(BLANK_ALLERGY_FORM);
  const [deleteAllergyConfirm, setDeleteAllergyConfirm] = useState(null);
  const [discontinueModal, setDiscontinueModal] = useState(null);
  const [deleteRxConfirm, setDeleteRxConfirm] = useState(null);
  const [discontinueReason, setDiscontinueReason] = useState("");

  const active       = prescriptions.filter(r => r.status === "active");
  const discontinued = prescriptions.filter(r => r.status === "discontinued" || r.discontinued_at);
  const hasNKDA  = allergies.length === 0;

  const openAddAllergy = () => {
    setAllergyForm(BLANK_ALLERGY_FORM);
    setEditingAllergy(null);
    setAllergyFormMode("add");
  };

  const openEditAllergy = (a) => {
    setAllergyForm({
      allergen: a.allergen || "",
      reaction: a.reaction || "",
      severity: a.severity || "Mild",
      allergy_type: a.allergy_type || "Drug",
      onset_date: a.onset_date || TODAY,
    });
    setEditingAllergy(a);
    setAllergyFormMode("edit");
  };

  const closeAllergyForm = () => {
    setAllergyFormMode(null);
    setEditingAllergy(null);
  };

  const handleSaveAllergy = async () => {
    if (!allergyForm.allergen.trim()) return;
    if (allergyFormMode === "add") {
      onAddAllergy({ ...allergyForm, id: `AL${Date.now()}` });
    } else {
      // allergy_type is form-only — not a DB column (matches PatientChartPage
      // handleSaveAllergyModal, the working reference).
      await db.from("allergies").update({
        allergen: allergyForm.allergen,
        reaction: allergyForm.reaction,
        severity: allergyForm.severity,
        onset_date: allergyForm.onset_date || null,
      }).eq("id", editingAllergy.id);
      setAllergies(a => a.map(x => x.id === editingAllergy.id ? { ...x, ...allergyForm } : x));
    }
    closeAllergyForm();
  };

  const handleDeleteAllergy = async () => {
    if (!deleteAllergyConfirm) return;
    await db.from("allergies").delete().eq("id", deleteAllergyConfirm.id);
    setAllergies(a => a.filter(x => x.id !== deleteAllergyConfirm.id));
    setDeleteAllergyConfirm(null);
  };

  const handleDiscontinue = async () => {
    if (!discontinueModal) return;
    await db.from("prescriptions").update({
      status: "discontinued",
      discontinued_at: new Date().toISOString(),
      discontinued_reason: discontinueReason,
    }).eq("id", discontinueModal.id);
    logAudit('discontinue', 'medication', discontinueModal.id, discontinueModal.patient_id, { drug: discontinueModal.drug_name, reason: discontinueReason });
    onDiscontinue(discontinueModal.id, discontinueReason);
    setDiscontinueModal(null);
    setDiscontinueReason("");
  };

  const handleDeleteRx = async () => {
    if (!deleteRxConfirm) return;
    await db.from("prescriptions").delete().eq("id", deleteRxConfirm.id);
    onDeleteRx(deleteRxConfirm.id);
    setDeleteRxConfirm(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display:"flex", gap:6 }}>
        <button className="btn btn-primary btn-sm" style={{ flex:1 }} onClick={onNewRx}>+ New Rx</button>
        <button className="btn btn-secondary btn-sm" style={{ padding:"5px 10px", fontSize:11, whiteSpace:"nowrap" }} onClick={onOrderSet} title="Medication Order Sets">📦 Sets</button>
      </div>

      {showAddMed && (
        <AddMedicationModal
          patient={patient}
          onSaved={onMedAdded}
          onClose={() => setShowAddMed(false)}
        />
      )}

      {/* Active medications */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        <div style={{ padding: "8px 14px 4px", display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em", flex: 1 }}>
            Active Medications ({active.length})
          </span>
          <button
            onClick={() => setShowAddMed(true)}
            style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, border: `1px solid ${C.teal}`, background: 'transparent', color: C.teal, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            + Add Med
          </button>
        </div>
        {active.length === 0 && (
          <div style={{ padding: "12px 14px", fontSize: 12, color: C.text3 }}>No active medications</div>
        )}
        {active.map(rx => {
          const sc = rx.is_controlled ? scheduleColor(rx.schedule) : null;
          const col = rxStatusColor(rx);
          return (
            <div key={rx.id} style={{
              padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
              borderLeft: `2px solid ${col}`,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }} onClick={() => onReorder(rx)} className="cursor-pointer">
                <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.text1 }}>{rx.drug_name}</div>
                  <div style={{ fontSize: 11, color: C.text3 }}>{rx.strength} · {rx.dosage_form}</div>
                </div>
                {rx.is_controlled && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: sc.color, background: sc.bg, padding: "1px 6px", borderRadius: 4, flexShrink: 0 }}>
                    {rx.schedule}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.text2, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {rx.sig}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: 11 }}>
                <span style={{ color: col }}>
                  {rx.refills_remaining === 0 ? "No refills" : `${rx.refills_remaining} refill${rx.refills_remaining !== 1 ? "s" : ""}`}
                </span>
                <span style={{ color: C.text3 }}>Last {fmtDate(rx.last_filled)}</span>
              </div>
              {/* Action buttons */}
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button onClick={e => { e.stopPropagation(); setDiscontinueModal(rx); setDiscontinueReason(""); }}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, border: `1px solid ${C.amber}`, background: "transparent", color: C.amber, cursor: "pointer" }}>
                  Discontinue
                </button>
                <button onClick={e => { e.stopPropagation(); setDeleteRxConfirm(rx); }}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, border: `1px solid ${C.red}`, background: "transparent", color: C.red, cursor: "pointer" }}>
                  Delete
                </button>
              </div>
            </div>
          );
        })}

        {/* Discontinued medications */}
        {discontinued.length > 0 && (
          <>
            <div style={{ padding: "10px 14px 4px", fontSize: 10, fontWeight: 600, color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 8, borderTop: `1px solid ${C.border}` }}>
              Discontinued ({discontinued.length})
            </div>
            {discontinued.map(rx => (
              <div key={rx.id} style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}`, opacity: 0.6, borderLeft: `2px solid ${C.text3}` }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: C.text2 }}>{rx.drug_name} {rx.strength}</div>
                <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                  Discontinued {rx.discontinued_at ? fmtDate(rx.discontinued_at.split("T")[0]) : ""}
                  {rx.discontinued_reason ? ` — ${rx.discontinued_reason}` : ""}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Allergies */}
        <div style={{ padding: "10px 14px 4px", fontSize: 10, fontWeight: 600, color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 8, borderTop: `1px solid ${C.border}` }}>
          Allergies
        </div>
        {hasNKDA && (
          <div style={{ padding: "6px 14px" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: C.greenLt, padding: "2px 8px", borderRadius: 4 }}>NKDA</span>
          </div>
        )}
        {allergies.map(a => (
          <div key={a.id} style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${severityBorder[a.severity] || C.text3}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: C.text1, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.allergen}</span>
              <span className={`badge ${severityColor[a.severity] || "badge-gray"}`} style={{ fontSize: 9, flexShrink: 0 }}>{a.severity}</span>
              <button onClick={e => { e.stopPropagation(); openEditAllergy(a); }}
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 12, padding: '2px 4px', flexShrink: 0 }} title="Edit allergy">✏</button>
              <button onClick={e => { e.stopPropagation(); setDeleteAllergyConfirm(a); }}
                style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 13, padding: '2px 4px', flexShrink: 0 }} title="Delete allergy">🗑</button>
            </div>
            <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{a.reaction}{a.allergy_type ? ` · ${a.allergy_type}` : ""}</div>
          </div>
        ))}
        <div style={{ padding: "6px 14px" }}>
          {!allergyFormMode ? (
            <button className="btn btn-ghost btn-sm" onClick={openAddAllergy}>+ Add Allergy</button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7, padding: "8px 0" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.text3, marginBottom: 2 }}>
                {allergyFormMode === "add" ? "New Allergy" : `Edit: ${editingAllergy?.allergen}`}
              </div>
              <input className="field-input" placeholder="Allergen (e.g. Penicillin)" value={allergyForm.allergen}
                onChange={e => setAllergyForm(f => ({ ...f, allergen: e.target.value }))} autoFocus />
              <input className="field-input" placeholder="Reaction (e.g. Anaphylaxis)" value={allergyForm.reaction}
                onChange={e => setAllergyForm(f => ({ ...f, reaction: e.target.value }))} />
              <select className="field-input field-select" value={allergyForm.severity}
                onChange={e => setAllergyForm(f => ({ ...f, severity: e.target.value }))}>
                {["Mild","Moderate","Severe","Life-threatening"].map(s => <option key={s}>{s}</option>)}
              </select>
              <select className="field-input field-select" value={allergyForm.allergy_type}
                onChange={e => setAllergyForm(f => ({ ...f, allergy_type: e.target.value }))}>
                {ALLERGY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <div>
                <div style={{ fontSize: 10, color: C.text3, marginBottom: 3 }}>Date Identified</div>
                <input className="field-input" type="date" value={allergyForm.onset_date}
                  onChange={e => setAllergyForm(f => ({ ...f, onset_date: e.target.value }))} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSaveAllergy} disabled={!allergyForm.allergen.trim()}>
                  {allergyFormMode === "add" ? "Add" : "Save"}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={closeAllergyForm}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Allergy Confirmation Modal */}
      {deleteAllergyConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#162436', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: 24, width: 'min(400px, 92vw)', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: C.text1, marginBottom: 8 }}>Delete Allergy?</div>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 6 }}>
              Allergen: <strong style={{ color: C.text1 }}>{deleteAllergyConfirm.allergen}</strong>
            </div>
            <div style={{ fontSize: 12, color: C.red, marginBottom: 20 }}>This cannot be undone.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteAllergyConfirm(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: C.red, borderColor: C.red, color: "#fff" }} onClick={handleDeleteAllergy}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Discontinue Medication Modal */}
      {discontinueModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#162436', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: 24, width: 'min(440px, 92vw)', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: C.text1, marginBottom: 8 }}>Discontinue Medication</div>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 16 }}>
              <strong style={{ color: C.text1 }}>{discontinueModal.drug_name} {discontinueModal.strength}</strong>
            </div>
            <div className="field" style={{ marginBottom: 16 }}>
              <div className="field-label">Reason for discontinuation</div>
              <input className="field-input" value={discontinueReason} onChange={e => setDiscontinueReason(e.target.value)} placeholder="Enter reason…" />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setDiscontinueModal(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: C.amber, borderColor: C.amber, color: "#fff" }} onClick={handleDiscontinue}>Discontinue</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Rx Confirmation Modal */}
      {deleteRxConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#162436', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: 24, width: 'min(400px, 92vw)', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: C.text1, marginBottom: 8 }}>Delete this medication?</div>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 6 }}>
              <strong style={{ color: C.text1 }}>{deleteRxConfirm.drug_name} {deleteRxConfirm.strength}</strong>
            </div>
            <div style={{ fontSize: 12, color: C.red, marginBottom: 20 }}>This is for accidental entries only.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteRxConfirm(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: C.red, borderColor: C.red, color: "#fff" }} onClick={handleDeleteRx}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SIG SHORTCUTS ────────────────────────────────────────────────────────────
const SIG_SHORTCUTS = ["QD","BID","TID","QID","PRN","q4-6h","q6h","q8h","q12h","HS","AC","PC","with food","with water"];

// ─── CENTER PANEL — PRESCRIPTION WRITER ──────────────────────────────────────
const EMPTY_RX = {
  drug_name:"", generic_name:"", strength:"", dosage_form:"tablet",
  sig:"", quantity:"", days_supply:"", refills:0,
  is_controlled:false, schedule:"", dea_number:"RW1234563",
  diagnosis_code:"", pharmacy_name:"", pharmacy_fax:"",
  notes:"", allergy_checked:false, pdmp_checked:false,
};

function CenterPanel({ patient, allergies, onSaved, initialValues, preferredPharmacy }) {
  const [form,        setForm]        = useState(() => ({
    ...EMPTY_RX,
    ...(initialValues || {}),
    pharmacy_name: initialValues?.pharmacy_name || preferredPharmacy?.pharmacy_name || "",
    pharmacy_fax:  initialValues?.pharmacy_fax  || preferredPharmacy?.fax || "",
  }));
  const [drugQuery,   setDrugQuery]   = useState(initialValues?.drug_name || "");
  const [diagQuery,   setDiagQuery]   = useState("");
  const drugInputRef = useRef(null);

  // ── Favorites ──
  const [favorites,   setFavorites]   = useState([]);
  const [showFavs,    setShowFavs]    = useState(true);
  const appCtx = useApp();
  const practiceId = patient?.practice_id || appCtx?.practiceId || null;
  const showNotification = appCtx?.showNotification ?? (() => {});

  useEffect(() => {
    db.from("favorite_medications")
      .select("*")
      .eq("practice_id", practiceId)
      .order("times_used", { ascending: false })
      .then(({ data }) => { if (data) setFavorites(data); });
  }, [practiceId]);

  const isFavorited = (medName) => favorites.some(f => f.drug_name === medName);

  const toggleFavorite = async (med, e) => {
    e.stopPropagation();
    const existing = favorites.find(f => f.drug_name === med.name);
    if (existing) {
      await db.from("favorite_medications").delete().eq("id", existing.id);
      setFavorites(favs => favs.filter(f => f.id !== existing.id));
    } else {
      const row = {
        practice_id: practiceId,
        provider: "Dr. Walker",
        drug_name: med.name,
        dose: med.commonDoses[0] || "",
        sig: med.commonSigs[0] || "",
        times_used: 0,
      };
      const { data } = await db.from("favorite_medications").insert([row]).select().single();
      if (data) setFavorites(favs => [...favs, data]);
    }
  };

  const loadFavorite = (fav) => {
    const med = MEDICATIONS.find(m => m.name === fav.drug_name);
    setForm(f => ({
      ...f,
      drug_name: fav.drug_name,
      generic_name: med?.genericName || fav.drug_name.toLowerCase(),
      is_controlled: med?.isControlled || false,
      schedule: med?.schedule || "",
      strength: fav.dose || med?.commonDoses[0] || "",
      dosage_form: med?.dosageForms[0] || "tablet",
      sig: fav.sig || med?.commonSigs[0] || "",
      quantity: med?.defaultQuantity || 30,
      days_supply: med?.defaultDaysSupply || 30,
      refills: (med?.isControlled && med?.schedule === "C-II") ? 0 : (med?.defaultRefills || 0),
      dea_number: med?.isControlled ? "RW1234563" : "",
    }));
    if (med) { setStrengths(med.commonDoses); setForms(med.dosageForms); setCommonSigs(med.commonSigs); }
    setDrugQuery(fav.drug_name);
    setShowDrugDrop(false);
    setSaved(false);
  };

  // Auto-focus and scroll to drug field on mount (triggers on New Rx / remount)
  useEffect(() => {
    if (drugInputRef.current) {
      drugInputRef.current.focus();
      drugInputRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);
  const [showDrugDrop,setShowDrugDrop]= useState(false);
  const [showDiagDrop,setShowDiagDrop]= useState(false);
  const [strengths,   setStrengths]   = useState([]);
  const [forms,       setForms]       = useState(["tablet"]);
  const [commonSigs,  setCommonSigs]  = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [allergyAlert,setAllergyAlert]= useState(null);  // { allergen, reaction }
  const [noAllergyWarn,setNoAllergyWarn] = useState(false);
  const [faxModal,    setFaxModal]    = useState(null);
  const [erxLoading,  setErxLoading]  = useState(false);
  const [erxModal,    setErxModal]    = useState(null);  // { ssoUrl, vendorLabel } — built via src/lib/erx.js vendor registry
  const epcsEnabled = appCtx?.currentProvider?.epcs_enabled || false;

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    // Force 0 refills for C-II
    if (k === "schedule" && v === "C-II") next.refills = 0;
    if (k === "refills" && form.schedule === "C-II") return { ...f, refills: 0 };
    return next;
  });

  const selectDrug = (med) => {
    setForm(f => ({
      ...f,
      drug_name: med.name,
      generic_name: med.genericName,
      is_controlled: med.isControlled,
      schedule: med.schedule || "",
      strength: med.commonDoses[0] || "",
      dosage_form: med.dosageForms[0] || "tablet",
      sig: med.commonSigs[0] || "",
      quantity: med.defaultQuantity,
      days_supply: med.defaultDaysSupply,
      refills: med.isControlled && med.schedule === "C-II" ? 0 : med.defaultRefills,
      dea_number: med.isControlled ? "RW1234563" : "",
    }));
    setStrengths(med.commonDoses);
    setForms(med.dosageForms);
    setCommonSigs(med.commonSigs);
    setDrugQuery(med.name);
    setShowDrugDrop(false);
  };

  const loadTemplate = (tpl) => {
    const med = MEDICATIONS.find(m => m.name === tpl.drug_name || m.genericName === tpl.drug_name.toLowerCase());
    setForm({ ...EMPTY_RX, ...tpl, dea_number: tpl.is_controlled ? "RW1234563" : "", generic_name: tpl.drug_name.toLowerCase() });
    if (med) { setStrengths(med.commonDoses); setForms(med.dosageForms); setCommonSigs(med.commonSigs); }
    setDrugQuery(tpl.drug_name);
    setSaved(false);
  };

  const drugResultGroups = showDrugDrop && drugQuery.length >= 1
    ? groupByCategory(searchMedications(drugQuery, 20))
    : [];

  const diagResults = showDiagDrop && diagQuery.length >= 2
    ? ORTHO_ICD10.filter(x => x.desc.toLowerCase().includes(diagQuery.toLowerCase()) || x.code.toLowerCase().includes(diagQuery.toLowerCase())).slice(0, 6)
    : [];

  const checkAndSave = () => {
    // Allergy check
    if (allergies.length === 0) { setNoAllergyWarn(true); return; }
    const drugLower = form.drug_name.toLowerCase();
    const match = allergies.find(a => {
      const al = a.allergen.toLowerCase();
      return drugLower.includes(al) || al.includes(drugLower) ||
        (al === "nsaids" && ["meloxicam","naproxen","ibuprofen","diclofenac","celecoxib","ketorolac"].some(d => drugLower.includes(d)));
    });
    if (match) { setAllergyAlert({ allergen: match.allergen, reaction: match.reaction }); return; }
    doSave();
  };

  const doSave = async (allergyOverrideReason) => {
    setSaving(true);
    const row = {
      ...form,
      patient_id: patient.id,
      prescribed_date: TODAY,
      status: "active",
      refills_remaining: form.refills,
      allergy_checked: true,
      pdmp_checked: form.is_controlled ? form.pdmp_checked : false,
      provider: "Dr. Walker",
    };
    const { data, error } = await db.from("prescriptions").insert([row]).select().single();
    setSaving(false);
    setSaved(true);
    setAllergyAlert(null);
    setNoAllergyWarn(false);
    if (!error) logAudit('create', 'medication', (data || row).id, (data || row).patient_id, { drug: row.drug_name });
    // Increment times_used for favorited drugs
    const fav = favorites.find(f => f.drug_name === row.drug_name);
    if (fav) {
      const newCount = (fav.times_used || 0) + 1;
      await db.from("favorite_medications").update({ times_used: newCount }).eq("id", fav.id);
      setFavorites(favs => favs.map(f => f.id === fav.id ? { ...f, times_used: newCount } : f));
    }
    if (onSaved) onSaved(data || row);
    setTimeout(() => setSaved(false), 3000);
  };

  const addSigShortcut = (abbr) => {
    setForm(f => ({ ...f, sig: f.sig ? f.sig + " " + abbr : abbr }));
  };

  const isValid = form.drug_name && form.strength && form.sig && form.quantity;
  const needsPdmp = form.is_controlled && !form.pdmp_checked;

  const handleFaxToPharmacy = async (prescription) => {
    const pharmacyName = prescription.pharmacy_name || preferredPharmacy?.pharmacy_name || 'pharmacy on file';
    const pharmacyFax  = prescription.pharmacy_fax  || preferredPharmacy?.fax || 'fax number on file';
    const confNum = 'FAX-' + Math.random().toString(36).substr(2, 8).toUpperCase();

    const faxRecord = {
      patient_id:          patient.id,
      direction:           'outbound',
      recipient_name:      pharmacyName,
      recipient_fax:       pharmacyFax,
      subject:             `Prescription — ${prescription.drug_name} ${prescription.strength}`,
      fax_body:            `PRESCRIPTION\n\nPatient: ${patient.first} ${patient.last}\nDOB: ${patient.dob}\n\nDrug: ${prescription.drug_name} ${prescription.strength}\nSig: ${prescription.sig}\nQty: ${prescription.quantity}\nRefills: ${prescription.refills}\n\nPrescribed by: ${prescription.provider || 'Dr. Walker'}\nDate: ${new Date().toLocaleDateString()}\n\nDEA: RW1234563`,
      status:              'sent',
      pages:               1,
      sent_at:             new Date().toISOString(),
      confirmation_number: confNum,
    };

    try {
      await db.from('faxes').insert(faxRecord);
    } catch (err) {
      console.error('Fax error:', err);
    }

    notify(`Prescription faxed to ${pharmacyName} (${pharmacyFax}) — confirmation ${confNum}`, 'success');
  };

  const handleSendErx = async () => {
    const vendor = getErxVendor();
    setErxLoading(true);
    try {
      // Step 1 — get vendor SSO token + config from server
      const authRes = await apiFetch(vendor.authEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id:  patient.id,
          practice_id: practiceId,
        }),
      });
      const authData = await authRes.json();
      if (!authRes.ok) throw new Error(authData.error || `${vendor.label} auth failed`);

      // Step 2 — sync patient to the vendor if no existing vendor patient id
      let extPatientId = authData[vendor.patientIdKey] || null;
      if (!extPatientId) {
        const syncRes = await apiFetch(vendor.syncEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patient_id:  patient.id,
            practice_id: practiceId,
          }),
        });
        const syncData = await syncRes.json();
        if (syncRes.ok && syncData[vendor.patientIdKey]) {
          extPatientId = syncData[vendor.patientIdKey];
        }
      }

      // Step 3 — build the vendor iframe URL and open the modal
      const ssoUrl = vendor.buildSsoUrl(authData, extPatientId);
      if (!ssoUrl) throw new Error(`${vendor.label} is not configured yet`);
      setErxModal({ ssoUrl, vendorLabel: vendor.label });
    } catch (err) {
      console.error('[handleSendErx]', err);
      showNotification(`e-Rx error: ${err.message}`, 'error');
    } finally {
      setErxLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Pre-fill banner — shown when opened with Approve with Modifications */}
      {initialValues && (
        <div style={{ padding: "8px 16px", background: C.amberLt, borderBottom: `1px solid ${C.amber}40`, fontSize: 12, color: C.amber, fontWeight: 500 }}>
          ✏️ Editing for approval — modify dose, quantity, or refills then save
        </div>
      )}
      {/* Template cards */}
      <div style={{ overflowY: "auto", padding: "12px 16px", borderBottom: `1px solid ${C.border}`, maxHeight: 200 }}>
        {TEMPLATES.map(cat => (
          <div key={cat.category} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
              {cat.category}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {cat.items.map(tpl => (
                <button key={tpl.drug_name + tpl.strength} onClick={() => loadTemplate(tpl)} style={{
                  padding: "4px 10px", borderRadius: 6, border: `1px solid ${cat.color}40`,
                  background: "transparent", color: cat.color, fontSize: 11, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5,
                  transition: "all 0.15s",
                }}>
                  {tpl.is_controlled && <span style={{ fontSize: 9, fontWeight: 700 }}>{tpl.schedule}</span>}
                  {tpl.drug_name} {tpl.strength}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Form */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
        {saved && (
          <div className="notification notification-info slide-in" style={{ marginBottom: 12 }}>
            <span>✓</span><span>Prescription saved successfully</span>
          </div>
        )}

        {/* PDMP banner */}
        {form.is_controlled && (
          <div className="slide-in" style={{
            background: C.amberLt, border: `1px solid ${C.amber}40`,
            borderRadius: 8, padding: "12px 16px", marginBottom: 14,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ fontSize: 18 }}>⚠</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: C.amber, fontSize: 13 }}>
                  CONTROLLED SUBSTANCE — {form.schedule} — Arizona PDMP Required
                </div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>
                  Arizona law requires PDMP verification via NarxCare before prescribing controlled substances.{" "}
                  <a href="https://narxcare.com" target="_blank" rel="noopener noreferrer" style={{ color: C.teal }}>
                    Check NarxCare ↗
                  </a>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: "pointer", fontSize: 12, color: C.text1 }}>
                  <input type="checkbox" checked={form.pdmp_checked} onChange={e => set("pdmp_checked", e.target.checked)} />
                  I have checked the Arizona PDMP (NarxCare) for this patient
                </label>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* ⭐ Favorites section */}
          <div style={{ gridColumn: "1 / -1" }}>
            <button onClick={() => setShowFavs(v => !v)} style={{
              display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
              color: C.amber, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "4px 0", marginBottom: 4,
            }}>
              <span style={{ fontSize: 13 }}>{showFavs ? "▾" : "▸"}</span>
              ⭐ Favorites
            </button>
            {showFavs && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, minHeight: 28 }}>
                {favorites.length === 0 ? (
                  <span style={{ fontSize: 11, color: C.text3, fontStyle: "italic" }}>
                    No favorites yet — star a medication to add it here
                  </span>
                ) : (
                  favorites.map(fav => (
                    <div key={fav.id} style={{
                      display: "flex", alignItems: "center", gap: 4,
                      background: C.navy3, border: `1px solid ${C.amber}40`,
                      borderRadius: 6, padding: "3px 6px 3px 8px", cursor: "pointer",
                    }}>
                      <span onClick={() => loadFavorite(fav)} style={{ fontSize: 11, color: C.amber, fontWeight: 500 }}>
                        {fav.drug_name} {fav.dose}
                      </span>
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        await db.from("favorite_medications").delete().eq("id", fav.id);
                        setFavorites(favs => favs.filter(f => f.id !== fav.id));
                      }} style={{
                        background: "none", border: "none", color: C.text3, fontSize: 12,
                        cursor: "pointer", padding: "0 2px", lineHeight: 1,
                      }}>✕</button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Drug name */}
          <div className="field" style={{ gridColumn: "1 / -1", position: "relative" }}>
            <div className="field-label">Drug Name</div>
            <div className="search-wrap">
              <span className="search-icon">⌕</span>
              <input ref={drugInputRef} className="search-input" value={drugQuery} placeholder="Search medications…"
                onChange={e => { setDrugQuery(e.target.value); setShowDrugDrop(true); set("drug_name", e.target.value); }}
                onFocus={() => setShowDrugDrop(true)} />
            </div>
            {drugResultGroups.length > 0 && (
              <div className="search-results" style={{ zIndex: 30, maxHeight: 320, overflowY: "auto" }}>
                {drugResultGroups.map(group => (
                  <div key={group.category}>
                    <div style={{ padding: "4px 12px 2px", fontSize: 9, fontWeight: 700, color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em", background: C.navy3, position: "sticky", top: 0 }}>
                      {group.category}
                    </div>
                    {group.items.map(med => (
                      <div key={med.name} className="search-result-item" onClick={() => selectDrug(med)}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 13, color: C.text1 }}>{med.name}</span>
                          {med.brandNames.length > 0 && (
                            <span style={{ fontSize: 11, color: C.text3, marginLeft: 6 }}>({med.brandNames[0]})</span>
                          )}
                          <div style={{ fontSize: 10, color: C.text3, marginTop: 1 }}>{med.commonDoses.join(', ')}</div>
                        </div>
                        {med.isControlled && (
                          <span style={{ fontSize: 10, color: scheduleColor(med.schedule).color, background: scheduleColor(med.schedule).bg, padding: "1px 5px", borderRadius: 4, fontWeight: 700, flexShrink: 0 }}>
                            ⚠ {med.schedule}
                          </span>
                        )}
                        <button onClick={(e) => toggleFavorite(med, e)} style={{
                          background: "none", border: "none", cursor: "pointer", fontSize: 15,
                          color: isFavorited(med.name) ? C.amber : C.text3,
                          padding: "0 2px", flexShrink: 0, lineHeight: 1,
                          transition: "color 0.15s",
                        }} title={isFavorited(med.name) ? "Remove from favorites" : "Add to favorites"}>
                          {isFavorited(med.name) ? "★" : "☆"}
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Strength */}
          <div className="field">
            <div className="field-label">Strength</div>
            {strengths.length > 0 ? (
              <select className="field-input field-select" value={form.strength} onChange={e => set("strength", e.target.value)}>
                {strengths.map(s => <option key={s}>{s}</option>)}
              </select>
            ) : (
              <input className="field-input" value={form.strength} onChange={e => set("strength", e.target.value)} placeholder="e.g. 5mg" />
            )}
          </div>

          {/* Dosage form */}
          <div className="field">
            <div className="field-label">Form</div>
            <select className="field-input field-select" value={form.dosage_form} onChange={e => set("dosage_form", e.target.value)}>
              {(forms.length > 0 ? forms : ["tablet","capsule","ODT","liquid","patch","cream","gel","injection","nasal spray","sublingual film","powder"]).map(f => <option key={f}>{f}</option>)}
            </select>
          </div>

          {/* Sig */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div className="field-label" style={{ marginBottom: 0 }}>Sig (Directions)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                {SIG_SHORTCUTS.map(abbr => (
                  <button key={abbr} onClick={() => addSigShortcut(abbr)} style={{
                    padding: "1px 6px", borderRadius: 4, border: `1px solid ${C.border2}`,
                    background: "transparent", color: C.text3, fontSize: 10, cursor: "pointer",
                  }}>{abbr}</button>
                ))}
              </div>
            </div>
            {commonSigs.length > 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                {commonSigs.map(s => (
                  <button key={s} onClick={() => set("sig", s)} style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 10, cursor: "pointer",
                    border: `1px solid ${form.sig === s ? C.teal : C.border2}`,
                    background: form.sig === s ? C.tealLt : "transparent",
                    color: form.sig === s ? C.teal : C.text3,
                  }}>{s}</button>
                ))}
              </div>
            )}
            <input className="field-input" value={form.sig} onChange={e => set("sig", e.target.value)} placeholder="e.g. 1 tab q6h PRN pain" />
          </div>

          {/* Quantity / Days / Refills */}
          <div className="field">
            <div className="field-label">Quantity</div>
            <input type="number" className="field-input" value={form.quantity} onChange={e => set("quantity", e.target.value)} placeholder="#" />
          </div>
          <div className="field">
            <div className="field-label">Days Supply</div>
            <input type="number" className="field-input" value={form.days_supply} onChange={e => set("days_supply", e.target.value)} placeholder="days" />
          </div>
          <div className="field">
            <div className="field-label">
              Refills {form.schedule === "C-II" && <span style={{ color: C.red, fontSize: 10 }}>(C-II: 0 only)</span>}
            </div>
            <input type="number" className="field-input" value={form.refills}
              min={0} max={form.schedule === "C-II" ? 0 : 11}
              onChange={e => set("refills", Math.min(parseInt(e.target.value) || 0, form.schedule === "C-II" ? 0 : 11))} />
          </div>

          {/* Controlled toggle */}
          <div className="field">
            <div className="field-label">Controlled Substance</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 6 }}>
              <input type="checkbox" checked={form.is_controlled} onChange={e => set("is_controlled", e.target.checked)} />
              <span style={{ fontSize: 13, color: form.is_controlled ? C.red : C.text2 }}>
                {form.is_controlled ? "Yes — controlled" : "No"}
              </span>
            </label>
          </div>

          {/* Schedule + DEA — only when controlled */}
          {form.is_controlled && (
            <>
              <div className="field">
                <div className="field-label">Schedule</div>
                <select className="field-input field-select" value={form.schedule} onChange={e => set("schedule", e.target.value)}>
                  <option value="">Select…</option>
                  {["C-II","C-III","C-IV","C-V"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="field">
                <div className="field-label">DEA Number</div>
                <input className="field-input" style={{ fontFamily: "var(--mono)" }} value={form.dea_number}
                  onChange={e => set("dea_number", e.target.value)} placeholder="XXXXXXXXX" />
              </div>
            </>
          )}

          {/* Diagnosis */}
          <div className="field" style={{ gridColumn: "1 / -1", position: "relative" }}>
            <div className="field-label">Diagnosis (ICD-10)</div>
            <div className="search-wrap">
              <span className="search-icon">⌕</span>
              <input className="search-input" value={diagQuery} placeholder="Search diagnosis…"
                onChange={e => { setDiagQuery(e.target.value); setShowDiagDrop(true); }}
                onFocus={() => setShowDiagDrop(true)} />
            </div>
            {form.diagnosis_code && <div style={{ fontSize: 11, color: C.teal, marginTop: 4, fontFamily: "var(--mono)" }}>{form.diagnosis_code}</div>}
            {diagResults.length > 0 && (
              <div className="search-results" style={{ zIndex: 30 }}>
                {diagResults.map(d => (
                  <div key={d.code} className="search-result-item" onClick={() => {
                    setDiagQuery(d.desc); set("diagnosis_code", d.code); setShowDiagDrop(false);
                  }}>
                    <span>{d.desc}</span>
                    <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 11, color: C.text3 }}>{d.code}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pharmacy */}
          <div className="field">
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <div className="field-label" style={{ marginBottom: 0 }}>Pharmacy Name</div>
              {preferredPharmacy && (
                <button onClick={() => { set("pharmacy_name", preferredPharmacy.pharmacy_name); set("pharmacy_fax", preferredPharmacy.fax || ""); }}
                  style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3, border: `1px solid ${C.teal}40`, background: "transparent", color: C.teal, cursor: "pointer", marginLeft: "auto" }}>
                  ★ Use preferred
                </button>
              )}
            </div>
            <input className="field-input" value={form.pharmacy_name} onChange={e => set("pharmacy_name", e.target.value)} placeholder="Pharmacy name" />
          </div>
          <div className="field">
            <div className="field-label">Pharmacy Fax</div>
            <input className="field-input" style={{ fontFamily: "var(--mono)" }} value={form.pharmacy_fax} onChange={e => set("pharmacy_fax", e.target.value)} placeholder="XXX-XXX-XXXX" />
          </div>

          {/* Notes */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <div className="field-label">Notes</div>
            <textarea className="field-input" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Clinical notes…" style={{ resize: "none" }} />
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-primary" disabled={saving || !isValid || needsPdmp} onClick={checkAndSave}>
            {saving ? "Saving…" : "Save Prescription"}
          </button>
          <button className="btn btn-secondary" disabled={!form.drug_name} onClick={() => printRx({ ...form, prescribed_date: TODAY, provider: "Dr. Walker" }, patient)}>
            Print Rx
          </button>
          <button className="btn btn-secondary" disabled={!form.drug_name || (!form.pharmacy_fax && !preferredPharmacy?.fax)} onClick={() => handleFaxToPharmacy(form)}>
            Fax to Pharmacy
          </button>
          {form.is_controlled && !epcsEnabled ? (
            <button className="btn btn-secondary" disabled
              title="EPCS credentialing required — contact your administrator"
              style={{ opacity: 0.5, cursor: "not-allowed" }}>
              🔒 EPCS — pending credentialing
            </button>
          ) : (
            <button className="btn btn-secondary" disabled={!form.drug_name || erxLoading} onClick={handleSendErx}>
              {erxLoading ? "Loading…" : "Send e-Rx"}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => { setForm(EMPTY_RX); setDrugQuery(""); setDiagQuery(""); setSaved(false); }}>
            Clear
          </button>
          {needsPdmp && (
            <span style={{ fontSize: 11, color: C.amber, alignSelf: "center" }}>⚠ PDMP check required before saving</span>
          )}
        </div>
      </div>

      {/* Modals */}
      {allergyAlert && (
        <AllergyAlertModal
          allergen={allergyAlert.allergen}
          reaction={allergyAlert.reaction}
          onCancel={() => setAllergyAlert(null)}
          onOverride={(reason) => doSave(reason)}
        />
      )}
      {noAllergyWarn && (
        <NoAllergyModal
          onCancel={() => setNoAllergyWarn(false)}
          onConfirm={() => { setNoAllergyWarn(false); doSave(); }}
        />
      )}
      {faxModal && (
        <FaxModal rx={faxModal} onClose={() => setFaxModal(null)} />
      )}
      {erxModal && (
        <ERxModal
          patientId={patient.id}
          providerId={practiceId}
          ssoUrl={erxModal.ssoUrl}
          vendorLabel={erxModal.vendorLabel}
          onClose={(status) => {
            setErxModal(null);
            const msg =
              status === 'sent'     ? 'Prescription sent to pharmacy' :
              status === 'received' ? 'Prescription received by pharmacy' :
              status === 'error'    ? `e-Rx error — verify in ${erxModal.vendorLabel} or use fax as fallback` :
                                      'e-Rx submitted — awaiting pharmacy confirmation';
            const type =
              status === 'sent' || status === 'received' ? 'success' :
              status === 'error' ? 'error' : 'info';
            showNotification(msg, type);
          }}
        />
      )}
    </div>
  );
}

// ─── RIGHT PANEL ──────────────────────────────────────────────────────────────
function RightPanel({ patientId, prescriptions, onApprove, onReorder, onApproveWithMods }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const refillReqs   = (isDemoMode ? MOCK_REFILL_REQUESTS : []).filter(r => r.patient_id === patientId);
  const history      = prescriptions.filter(r => r.status !== "active");
  const [denyTarget, setDenyTarget] = useState(null);
  const [dismissed,  setDismissed]  = useState([]);

  const activeReqs = refillReqs.filter(r => !dismissed.includes(r.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Refill requests */}
      <div style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ padding: "8px 14px 4px", fontSize: 10, fontWeight: 600, color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Refill Requests ({activeReqs.length})
        </div>
        {activeReqs.length === 0 && (
          <div style={{ padding: "10px 14px", fontSize: 12, color: C.text3 }}>No pending refill requests</div>
        )}
        {activeReqs.map(req => (
          <div key={req.id} style={{
            padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
            borderLeft: `2px solid ${req.is_controlled ? C.red : C.amber}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: C.text1 }}>{req.drug_name}</div>
            <div style={{ fontSize: 11, color: C.text3, marginBottom: 6 }}>
              {req.pharmacy} · {req.days_since_last_fill}d since last fill
              {req.is_controlled && <span style={{ color: C.red, fontWeight: 600 }}> · {req.schedule}</span>}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }}
                onClick={() => { onApprove(req); setDismissed(d => [...d, req.id]); }}>
                ✅ Approve
              </button>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, borderColor: C.red, color: C.red }}
                onClick={() => setDenyTarget(req)}>
                ❌ Deny
              </button>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, borderColor: C.amber, color: C.amber }}
                onClick={() => { onApproveWithMods && onApproveWithMods(req); setDismissed(d => [...d, req.id]); }}>
                ✏️ Modify
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Prescription history */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        <div style={{ padding: "10px 14px 4px", fontSize: 10, fontWeight: 600, color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          History ({history.length})
        </div>
        {history.length === 0 && (
          <div style={{ padding: "10px 14px", fontSize: 12, color: C.text3 }}>No prescription history</div>
        )}
        {history.map(rx => (
          <div key={rx.id} style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: C.text2 }}>{rx.drug_name} {rx.strength}</span>
              <span className={`badge ${rx.status === "completed" ? "badge-gray" : "badge-red"}`} style={{ fontSize: 9, marginLeft: "auto" }}>
                {rx.status}
              </span>
            </div>
            <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
              {fmtDate(rx.prescribed_date)} · Qty {rx.quantity} · {rx.refills} refills
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 4, fontSize: 10 }} onClick={() => onReorder(rx)}>
              Reorder ↗
            </button>
          </div>
        ))}
      </div>

      {denyTarget && (
        <DenyRefillModal
          request={denyTarget}
          onDeny={(reason) => { setDismissed(d => [...d, denyTarget.id]); }}
          onClose={() => setDenyTarget(null)}
        />
      )}
    </div>
  );
}

// ─── MAIN MEDICATIONS TAB ────────────────────────────────────────────────────
export default function MedicationsTab({ patient, allergies: propAllergies, setAllergies: propSetAllergies, preferredPharmacy }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const mockRx       = isDemoMode ? MOCK_PRESCRIPTIONS.filter(r => r.patient_id === patient.id) : [];
  const mockAllerg   = isDemoMode ? (MOCK_ALLERGIES[patient.id] || []) : [];

  const [prescriptions, setPrescriptions] = useState([]);
  const [localAllergies, setLocalAllergies] = useState(mockAllerg);
  const allergies    = propAllergies    ?? localAllergies;
  const setAllergies = propSetAllergies ?? setLocalAllergies;
  const [formKey,       setFormKey]       = useState(0);
  const [prefilledRx,   setPrefilledRx]   = useState(null);
  const [showOrderSets, setShowOrderSets] = useState(false);

  useEffect(() => {
    if (!patient?.id || !/^[0-9a-f-]{36}$/.test(patient.id)) return;
    db.from("prescriptions").select("id, patient_id, drug_name, strength, dosage_form, sig, quantity, refills, prescribed_date, provider, status, notes, practice_id").eq("patient_id", patient.id)
      .order("prescribed_date", { ascending: false }).limit(100)
      .then(({ data }) => setPrescriptions(data?.length ? data : mockRx));
  }, [patient.id]);

  const { active, history } = useMemo(() => ({
    active:  prescriptions.filter(r => r.status === "active"),
    history: prescriptions.filter(r => r.status !== "active" && r.status !== "discontinued" && !r.discontinued_at),
  }), [prescriptions]);

  const handleSaved = (row) => {
    setPrescriptions(p => [{ ...row, status: "active" }, ...p]);
  };

  const handleReorder = (rx) => {
    setFormKey(k => k + 1);
  };

  const handleApprove = (req) => {
    const existing = prescriptions.find(r => r.id === req.rx_id);
    if (!existing) return;
    setPrescriptions(p => p.map(r => r.id === req.rx_id ? { ...r, refills_remaining: Math.max(0, (r.refills_remaining || 1) - 1), last_filled: TODAY } : r));
  };

  const handleAddAllergy = async (allergy) => {
    // Strip form-only fields before insert (allergy_type is not a DB column;
    // id is a local "AL{ts}" placeholder; "" is not a valid DATE) — matches
    // PatientChartPage handleAddAllergy, the working reference.
    const { allergy_type, id, onset_date, ...rest } = allergy;
    const row = { ...rest, onset_date: onset_date || null, patient_id: patient.id };
    await db.from("allergies").insert([row]);
    setAllergies(a => [...a, { ...allergy, patient_id: patient.id }]);
  };

  const handleDiscontinue = (rxId, reason) => {
    setPrescriptions(p => p.map(r => r.id === rxId ? { ...r, status: "discontinued", discontinued_at: new Date().toISOString(), discontinued_reason: reason } : r));
  };

  const handleDeleteRx = (rxId) => {
    setPrescriptions(p => p.filter(r => r.id !== rxId));
  };

  const handleApproveWithMods = (req) => {
    const existing = prescriptions.find(r => r.id === req.rx_id);
    if (!existing) return;
    setPrefilledRx(existing);
    setFormKey(k => k + 1);
  };

  const colStyle = (width, extra = {}) => ({
    width, flexShrink: 0,
    borderRight: `1px solid ${C.border}`,
    height: "100%", overflow: "hidden",
    ...extra,
  });

  return (
    <div style={{
      display: "flex",
      height: "calc(100vh - var(--header-h) - 128px)",
      overflow: "hidden",
      margin: "-20px",
    }}>
      {showOrderSets && (
        <MedOrderSetsModal
          patient={patient}
          allergies={allergies}
          onSaved={handleSaved}
          onClose={() => setShowOrderSets(false)}
        />
      )}

      {/* Left — 25% */}
      <div style={colStyle("25%")}>
        <LeftPanel
          patient={patient}
          prescriptions={prescriptions}
          allergies={allergies}
          setAllergies={setAllergies}
          onNewRx={() => { setPrefilledRx(null); setFormKey(k => k + 1); }}
          onOrderSet={() => setShowOrderSets(true)}
          onReorder={handleReorder}
          onAddAllergy={handleAddAllergy}
          onDiscontinue={handleDiscontinue}
          onDeleteRx={handleDeleteRx}
          onMedAdded={handleSaved}
        />
      </div>

      {/* Center — 50% */}
      <div style={{ flex: 1, height: "100%", overflow: "hidden" }}>
        <CenterPanel key={formKey} patient={patient} allergies={allergies} onSaved={handleSaved} initialValues={prefilledRx} preferredPharmacy={preferredPharmacy} />
      </div>

      {/* Right — 25% */}
      <div style={{ ...colStyle("25%"), borderRight: "none" }}>
        <RightPanel
          patientId={patient.id}
          prescriptions={prescriptions}
          onApprove={handleApprove}
          onReorder={handleReorder}
          onApproveWithMods={handleApproveWithMods}
        />
      </div>
    </div>
  );
}
